import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { NavigationEnd, NavigationError, NavigationStart, Router, RouterOutlet } from '@angular/router';

import { onAuthStateChanged } from 'firebase/auth';
import { logEvent, setUserId, setUserProperties } from 'firebase/analytics';

import { partnerIdForOrg } from '@crewdle/white-label';
import { WhiteLabelService } from '@crewdle/white-label/angular';

import { environment } from 'src/environments/environment';

import { getBrowserLang, TranslocoService } from '@jsverse/transloco';
import { TranslocoModule } from '@jsverse/transloco';
import { FirebaseService } from './services/firebase/firebase.service';
import { AppUpdateService } from './services/app-update/app-update.service';
import { DataStoreService } from './services/data-store/data-store.service';
import { PhantomAuthService } from './services/phantom-auth/phantom-auth.service';
import { ThemeService } from './services/theme/theme.service';
import { LanguageService } from './services/language/language.service';
import { ToastContainerComponent } from './shared/ui/toast/toast-container.component';
import { ConfirmDialogComponent } from './shared/ui/confirm-dialog/confirm-dialog.component';
import { HomeHubOverlayComponent } from './components/home-hub-overlay/home-hub-overlay.component';
import { SupportChatbotComponent } from './components/support-chatbot/support-chatbot.component';
import { isChunkLoadError, reloadOnceForChunkError, registerChunkErrorRecovery } from './chunk-reload';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [RouterOutlet, CommonModule, TranslocoModule, ToastContainerComponent, ConfirmDialogComponent, HomeHubOverlayComponent, SupportChatbotComponent],
})
export class AppComponent {
  constructor(
    private firebase: FirebaseService,
    private appUpdate: AppUpdateService,
    private phantomAuth: PhantomAuthService,
    private themeService: ThemeService,
    private languageService: LanguageService,
    private translate: TranslocoService,
    private router: Router,
    private dataStore: DataStoreService,
    // Instantiated app-wide so partner white-label (theme/favicon/title)
    // applies on every route, including the ones without the sidebar.
    private whiteLabel: WhiteLabelService,
  ) {
    console.log('App version', environment.version);
    this.phantomAuth.start();

    // Recover from post-deploy chunk-load failures that happen outside the
    // router (dynamic imports, deferred views).
    registerChunkErrorRecovery();

    // Feed org changes to the white-label service. Only once an org is present:
    // the org resolves async after auth, and propagating the transient null
    // would clear the cached pre-paint brand mid-boot. Switching to a
    // partner-less org still clears (org non-null, partnerIdForOrg → null).
    this.dataStore.getState$().subscribe(({ newState }) => {
      if (newState.organization) {
        this.whiteLabel.setPartnerId(partnerIdForOrg(newState.organization));
      }
    });

    // Hold the index.html loader until branding is decided (partner or
    // Crewdle) so the chrome never flips brands in front of the user.
    // settled() is time-capped, so a slow read can't trap anyone here.
    void this.whiteLabel.settled().then(() => {
      document.getElementById('splash-screen')?.remove();
    });

    onAuthStateChanged(this.firebase.auth, (authUser) => {
      if (!authUser || authUser.isAnonymous || authUser.email === null) {
        this.themeService.applyTheme(void 0, false, false)
      }
      if (authUser && !authUser.isAnonymous) {
        const createdAt = authUser.metadata.creationTime ?? void 0;
        try {
          setUserId(this.firebase.analytics, authUser.uid);
          // Pseudonymous only: never send email or names to GA (a US processor).
          setUserProperties(this.firebase.analytics, { created_at: createdAt });
        } catch { /* analytics must never break the auth flow */ }
        this.themeService.applyTheme();
        this.initializeTranslation();
      } else {
        try { setUserId(this.firebase.analytics, null); } catch { /* ignore */ }
      }
    });

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart && this.appUpdate.updateAvailable) {
        location.reload();
        return;
      }
      if (event instanceof NavigationEnd) {
        try {
          logEvent(this.firebase.analytics, 'page_view', { page_path: event.urlAfterRedirects });
        } catch { /* ignore */ }
      }
      if (event instanceof NavigationError && isChunkLoadError(event.error)) {
        reloadOnceForChunkError();
      }
    });
  }

  private async initializeTranslation() {
    const browserLang = getBrowserLang();
    const userLang = await this.languageService.getLanguage();
    this.translate.setDefaultLang('en');

    if (userLang) {
      this.translate.setActiveLang(userLang);
    } else if (browserLang?.match(/en|fr/)) {
      this.translate.setActiveLang(browserLang);
    } else {
      this.translate.setActiveLang('en');
    }
  }
}
